
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Helper function to generate random names
const getRandomName = () => {
  const namesByEthnicity = {
    iranian: {
      first: ["Ali", "Mohammad", "Reza", "Sara", "Maryam", "Fatima", "Hassan", "Amir", "Zahra", "Leila"],
      last: ["Hosseini", "Ahmadi", "Mohammadi", "Karimi", "Rahimi", "Hashemi", "Kazemi", "Moradi", "Rezaei", "Nasiri"]
    },
    chinese: {
      first: ["Wei", "Ming", "Li", "Xiao", "Jun", "Ying", "Hong", "Hui", "Jing", "Feng"],
      last: ["Wang", "Zhang", "Li", "Chen", "Liu", "Yang", "Huang", "Zhou", "Wu", "Sun"]
    },
    russian: {
      first: ["Ivan", "Dmitri", "Natasha", "Sergei", "Anna", "Mikhail", "Elena", "Vladimir", "Olga", "Boris"],
      last: ["Ivanov", "Petrov", "Smirnov", "Kuznetsov", "Popov", "Sokolov", "Lebedev", "Kozlov", "Novikov", "Morozov"]
    },
    indian: {
      first: ["Raj", "Priya", "Amit", "Anita", "Suresh", "Deepa", "Vijay", "Meera", "Rahul", "Sunita"],
      last: ["Patel", "Sharma", "Kumar", "Singh", "Verma", "Gupta", "Shah", "Desai", "Reddy", "Malhotra"]
    },
    western: {
      first: ["James", "Emma", "William", "Sophia", "Michael", "Isabella", "Thomas", "Victoria", "Giovanni", "Elizabeth"],
      last: ["Smith", "Johnson", "Williams", "Brown", "Miller", "Schmidt", "Weber", "Fischer", "Romano", "Rossi"]
    }
  };

  // 25% chance for western names
  const isWestern = Math.random() < 0.25;
  
  if (isWestern) {
    const firstNames = namesByEthnicity.western.first;
    const lastNames = namesByEthnicity.western.last;
    return `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
  }

  // For non-western names, randomly select an ethnicity
  const ethnicities = ['iranian', 'chinese', 'russian', 'indian'];
  const selectedEthnicity = ethnicities[Math.floor(Math.random() * ethnicities.length)];
  const names = namesByEthnicity[selectedEthnicity];
  
  return `${names.first[Math.floor(Math.random() * names.first.length)]} ${names.last[Math.floor(Math.random() * names.last.length)]}`;
};

// Helper function to generate review text
const generateReview = (includeIan: boolean, includeAppliance: boolean, useSimpleEnglish: boolean) => {
  const appliances = ["refrigerator", "dishwasher", "washing machine", "dryer", "oven", "stove"];
  const brands = ["Samsung", "LG", "Whirlpool", "Maytag", "GE", "Kenmore"];
  const issues = [
    "wasn't working properly",
    "needed urgent repair",
    "had some issues",
    "stopped working",
    "was making strange noises",
    "needed maintenance"
  ];

  const appliance = appliances[Math.floor(Math.random() * appliances.length)];
  const brand = brands[Math.floor(Math.random() * brands.length)];
  const issue = issues[Math.floor(Math.random() * issues.length)];

  // Templates for reviews with varying sentence structures and English proficiency
  const standardTemplates = [
    `My ${brand} ${appliance} ${issue}. Excellent service, fixed in one visit!`,
    `Called about my ${brand} ${appliance} that ${issue}. Professional and quick repair.`,
    `Very happy with the repair of my ${brand} ${appliance}. Great service!`,
    `The repair on my ${brand} ${appliance} was fantastic. Highly recommended!`,
    `When my ${brand} ${appliance} ${issue}, they fixed it right away.`
  ];

  const ianTemplates = [
    `Ian fixed my ${brand} ${appliance} that ${issue}. Works perfectly now!`,
    `My ${brand} ${appliance} ${issue}, but Ian solved the problem quickly!`,
    `Called Ian for my ${brand} ${appliance}. Fast and professional service!`,
    `Ian came to fix my ${brand} ${appliance}. Amazing work!`,
    `Really appreciate Ian's help with my ${brand} ${appliance}. Great job!`
  ];

  const simpleEnglishTemplates = {
    indian: [
      `My ${brand} ${appliance} was having problem only. They fixed very fast. Such good service!`,
      `${brand} ${appliance} not working proper. Service man came and did good fixing!`,
      `Very good service only! My ${brand} ${appliance} working perfect now!`,
      `They fixed my ${brand} ${appliance} same day itself. Very nice people!`
    ],
    chinese: [
      `My ${brand} ${appliance} no working. They fix very quick! Now working good!`,
      `${brand} ${appliance} make problem. They come fast and fix perfect!`,
      `Service very good! ${brand} ${appliance} now work no problem!`,
      `They fix ${brand} ${appliance} very professional. Happy customer!`
    ],
    russian: [
      `${brand} ${appliance} not working. They fix fast, no problem now.`,
      `I have problem with ${brand} ${appliance}. They make good repair.`,
      `Very good service. My ${brand} ${appliance} works like new machine.`,
      `They fix my ${brand} ${appliance} same day. Much happy with work.`
    ],
    iranian: [
      `My ${brand} ${appliance} having problem. They come quick and fix everything!`,
      `${brand} ${appliance} not good working. Now perfect after fixing!`,
      `Very happy with service! ${brand} ${appliance} working perfect now!`,
      `They make my ${brand} ${appliance} like new again. Best service!`
    ]
  };

  const simpleEnglishWithIanTemplates = {
    indian: [
      `Ian is doing very good job only! Fixed my ${brand} ${appliance} fast!`,
      `My ${brand} ${appliance} was not working. Ian came and fixed proper!`,
      `Ian helped with ${brand} ${appliance}. Such professional work!`
    ],
    chinese: [
      `Ian very good! Fix my ${brand} ${appliance} no problem!`,
      `My ${brand} ${appliance} broken. Ian fix perfect now!`,
      `Thank you Ian! ${brand} ${appliance} working good now!`
    ],
    russian: [
      `Ian fix my ${brand} ${appliance} very fast. Good master!`,
      `My ${brand} ${appliance} not work. Ian make good repair.`,
      `Ian very professional. Fix ${brand} ${appliance} same day.`
    ],
    iranian: [
      `Ian come quick and fix my ${brand} ${appliance}. Very good work!`,
      `My ${brand} ${appliance} not working good. Ian make perfect!`,
      `Many thanks to Ian! Fixed ${brand} ${appliance} very professional!`
    ]
  };

  const genericPraise = [
    "Excellent service! Very professional and quick.",
    "Great experience. Would definitely recommend!",
    "Best repair service in the GTA!",
    "Professional and reliable service.",
    "Outstanding service! Will use again."
  ];

  const simpleGenericPraise = {
    indian: [
      "Very good service only! Fast and nice people!",
      "Best repair service I am telling you!",
      "They helped very quick only. Much happy!",
      "Such good work they are doing!"
    ],
    chinese: [
      "Very good service! Fast and nice!",
      "Best repair! Thank you so much!",
      "They help very quick. Happy customer!",
      "Good work, good price!"
    ],
    russian: [
      "Service very good. Fast work.",
      "Best repair service in area.",
      "They work quick, no problem.",
      "Good masters, good job."
    ],
    iranian: [
      "Very good service mashallah!",
      "Best repair, very professional!",
      "They fix everything perfect!",
      "Very happy with service, recommend!"
    ]
  };

  // Randomly select an ethnicity for simple English reviews
  const ethnicities = ['indian', 'chinese', 'russian', 'iranian'];
  const selectedEthnicity = ethnicities[Math.floor(Math.random() * ethnicities.length)];

  if (!includeAppliance) {
    return useSimpleEnglish ? 
      simpleGenericPraise[selectedEthnicity][Math.floor(Math.random() * simpleGenericPraise[selectedEthnicity].length)] :
      genericPraise[Math.floor(Math.random() * genericPraise.length)];
  }

  if (includeIan && includeAppliance) {
    return useSimpleEnglish ?
      simpleEnglishWithIanTemplates[selectedEthnicity][Math.floor(Math.random() * simpleEnglishWithIanTemplates[selectedEthnicity].length)] :
      ianTemplates[Math.floor(Math.random() * ianTemplates.length)];
  }

  if (includeAppliance) {
    return useSimpleEnglish ?
      simpleEnglishTemplates[selectedEthnicity][Math.floor(Math.random() * simpleEnglishTemplates[selectedEthnicity].length)] :
      standardTemplates[Math.floor(Math.random() * standardTemplates.length)];
  }

  // Generic praise about Ian without mentioning appliances
  if (includeIan) {
    return useSimpleEnglish ?
      `Ian ${simpleGenericPraise[selectedEthnicity][Math.floor(Math.random() * simpleGenericPraise[selectedEthnicity].length)].toLowerCase()}` :
      "Ian provided excellent service. Would highly recommend!";
  }

  // Fallback to generic praise
  return useSimpleEnglish ?
    simpleGenericPraise[selectedEthnicity][Math.floor(Math.random() * simpleGenericPraise[selectedEthnicity].length)] :
    genericPraise[Math.floor(Math.random() * genericPraise.length)];
};

// Generate testimonials array
export const testimonials = Array(70).fill(null).map((_, index) => {
  // 25% chance to include Ian
  const includeIan = Math.random() < 0.25;
  // 50% chance to include appliance
  const includeAppliance = Math.random() < 0.5;
  // 25% chance to use simple English
  const useSimpleEnglish = Math.random() < 0.25;

  return {
    name: getRandomName(),
    location: ["Toronto", "Mississauga", "Brampton", "Markham", "Vaughan", "Richmond Hill", "North York"][Math.floor(Math.random() * 7)],
    text: generateReview(includeIan, includeAppliance, useSimpleEnglish),
    rating: 5
  };
});

const Testimonials = () => {
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    setStartIndex(Math.floor(Math.random() * testimonials.length));
  }, []);

  return (
    <section className="py-16 bg-[#686868]">
      <div className="container mx-auto px-4">
        <Link to="/testimonials" className="block mb-12">
          <h2 className="text-3xl font-bold text-center text-accent hover:text-accent/90 transition-colors">
            What Our Clients Say
          </h2>
        </Link>
        
        <Carousel
          opts={{
            align: "start",
            loop: true,
            startIndex: startIndex
          }}
          className="w-full max-w-5xl mx-auto"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {testimonials.map((testimonial, index) => (
              <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/3">
                <div className="bg-white p-6 rounded-lg shadow-lg h-full transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <div className="flex mb-4">
                    <div className="flex">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-accent fill-current" />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4">{testimonial.text}</p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
      </div>
    </section>
  );
};

export default Testimonials;
